import type { GetAvailableGamesRow } from '@/tygo/db'
import {
  type CurrentUserResponse,
  ActionTypeBet,
  ActionTypeCall,
  ActionTypeCheck,
  ActionTypeRaise,
  type GameSeat,
  type GameStatus,
  GameStatusWaiting,
  GameStatusInProgress,
  GameStatusCompleted,
  GameStatusCancelled,
  type GetGameResponse,
} from '@/tygo/dto'

export const isCurrentUserSeat = (
  seat: GameSeat,
  currentUser: CurrentUserResponse | undefined,
): boolean => {
  if (!seat.player) return false

  return !!currentUser && seat.player.player_id === currentUser.user.id
}

export const canJoinGame = (
  game: GetAvailableGamesRow,
  user: CurrentUserResponse,
): boolean => {
  if (game.min_buy_in > user.balance) {
    return false
  }

  return true
}

const getCurrentUserSeatIndex = (
  currentUser: CurrentUserResponse,
  gameSeats: Array<GameSeat>,
): number => {
  return gameSeats.findIndex(
    (seat) => seat.player?.player_id === currentUser.user.id,
  )
}

export const calculateSeatPositions = (
  gameSeats: Array<GameSeat>,
  currentUser?: CurrentUserResponse,
): Map<number, { x: number; y: number }> => {
  const userOffset = currentUser
    ? (getCurrentUserSeatIndex(currentUser, gameSeats) * (2 * Math.PI)) /
      gameSeats.length
    : 0

  const positions = new Map<number, { x: number; y: number }>()

  for (let i = 0; i < gameSeats.length; i++) {
    const angle =
      Math.PI / 2 + (i * (2 * Math.PI)) / gameSeats.length - userOffset

    const rx = 45
    const ry = 45

    const x = 50 + rx * Math.cos(angle)
    const y = 50 + ry * Math.sin(angle)

    positions.set(gameSeats[i].seat_number, { x, y })
  }

  return positions
}

export const calculateBetOffset = (
  seatPosition: { x: number; y: number },
  distance: number = 130,
): { offsetX: number; offsetY: number } => {
  const dx = 50 - seatPosition.x
  const dy = 50 - seatPosition.y
  const magnitude = Math.sqrt(dx * dx + dy * dy)
  return {
    offsetX: (dx / magnitude) * distance,
    offsetY: (dy / magnitude) * distance,
  }
}

export const getChipImage = (microUsdc: number): string => {
  if (microUsdc < 100_000) return '/chips/grey_chip.png' // < $0.10
  if (microUsdc < 1_000_000) return '/chips/green_chip.png' // $0.10 - $0.99
  if (microUsdc < 5_000_000) return '/chips/blue_chip.png' // $1 - $4.99
  if (microUsdc < 25_000_000) return '/chips/red_chip.png' // $5 - $24.99
  if (microUsdc < 100_000_000) return '/chips/purple_chip.png' // $25 - $99.99
  if (microUsdc < 500_000_000) return '/chips/brown_chip.png' // $100 - $499.99
  return '/chips/pink_chip.png' // $500+
}

export const canCheck = (seat: GameSeat): boolean =>
  seat.player?.available_actions?.some(
    (action) => action.action_type === ActionTypeCheck,
  ) ?? false

export const canCall = (seat: GameSeat): boolean =>
  seat.player?.available_actions?.some(
    (action) => action.action_type === ActionTypeCall,
  ) ?? false

// Returns the additional amount the player needs to put in to call.
// The backend sends the total cumulative bet as the call amount,
// so we subtract the player's existing contribution this round.
export const getCallAmount = (seat: GameSeat): number | undefined => {
  const callAction = seat.player?.available_actions?.find(
    (a) => a.action_type === ActionTypeCall,
  )
  if (!callAction?.amount) return undefined
  const currentBet = seat.player?.current_bet ?? 0
  return callAction.amount - currentBet
}

export const canRaise = (seat: GameSeat): boolean =>
  seat.player?.available_actions?.some(
    (action) =>
      action.action_type === ActionTypeRaise ||
      action.action_type === ActionTypeBet,
  ) ?? false

export const placeLabel = (place: number): string => {
  if (place === 1) return '🥇'
  if (place === 2) return '🥈'
  if (place === 3) return '🥉'
  return String(place)
}

const handRankLabels: Record<string, string> = {
  high_card: 'High Card',
  one_pair: 'One Pair',
  two_pair: 'Two Pair',
  three_of_a_kind: 'Three of a Kind',
  straight: 'Straight',
  flush: 'Flush',
  full_house: 'Full House',
  four_of_a_kind: 'Four of a Kind',
  straight_flush: 'Straight Flush',
}

export const handRankLabel = (rank: string): string =>
  handRankLabels[rank] ?? rank

const gameStatusLabels: Record<GameStatus, string> = {
  [GameStatusWaiting]: 'Waiting',
  [GameStatusInProgress]: 'In Progress',
  [GameStatusCompleted]: 'Completed',
  [GameStatusCancelled]: 'Cancelled',
}

export const formatGameStatus = (status: GameStatus): string =>
  gameStatusLabels[status]

export const getAwaitingPlayersCount = (
  game: GetGameResponse | null,
): number | null => {
  if (!game || !game.auto_start_count) return null

  return (
    game.auto_start_count - game.game_seats.filter((seat) => seat.player).length
  )
}
